import React from 'react';

import { ErrorBoundary } from "react-error-boundary";

import { Button as MuiButton, ButtonProps as MuiButtonProps, 
    Card as MuiCard, CardActions as MuiCardActions, CardContent as MuiCardContent, CardMedia as MuiCardMedia, 
    Chip as MuiChip, ChipProps as MuiChipProps, 
    IconButton as MuiIconButton, IconButtonProps as MuiIconButtonProps, 
    Input as MuiInput, InputProps as MuiInputProps, 
    Typography, TypographyTypeMap 
} from '@material-ui/core';
import { Skeleton as MuiSkeleton, SkeletonProps as MuiSkeletonProps } from '@material-ui/lab';
import { useTheme, Theme, ThemeProvider } from '@material-ui/core/styles';

type ElementProps = { 
    align?: TypographyTypeMap['props']['align'], 
    color?: TypographyTypeMap['props']['color'], 
    paragraph?: TypographyTypeMap['props']['paragraph'], 
    style?: React.CSSProperties
} ;

export const Context = React.createContext<{
    chars: () => {
        bullet: string
    },
    palette?: () => {
        background: string,
        primary: { light: string, main: string, dark: string },
        secondary: { light: string, main: string, dark: string },
    },
    typography?: () => {
        heading: (props: React.PropsWithChildren<ElementProps>) => JSX.Element;
        body: (props: React.PropsWithChildren<ElementProps>) => JSX.Element;
        value: (props: React.PropsWithChildren<ElementProps>) => JSX.Element;
    }
}>({
    chars: () => ({
        bullet: '\u2022'
    })
});

export function Screen(props: React.PropsWithChildren<{
    prefix: string,
    inset?: React.CSSProperties['height'],
    theme: Theme
}>) {
    const _context = React.useContext(Context);
      
    return (
        <ErrorBoundary fallback={<div>Internal error: unable to render Styler.Screen</div>}>
            <ThemeProvider theme={props.theme}>
                <Context.Provider value={{
                    ..._context,
                    palette: () => ({
                        background: props.theme.palette.background.default,
                        primary: props.theme.palette.primary,
                        secondary: props.theme.palette.secondary
                    }),
                    typography: () => ({
                        heading: (props) => <Typography {...props} variant="h1" color={'primary'}>{props.children}</Typography>,
                        body: (props) => <Typography {...props} variant="body1" color={'primary'}>{props.children}</Typography>,
                        value: (props) => <Typography {...props} variant="body2" color={'primary'}>{props.children}</Typography>,
                    })
                
                }}><div id={`${props.prefix}-root`} style={{ width: '100%', height: '100%'}}>{props.children}</div></Context.Provider>
            </ThemeProvider>
        </ErrorBoundary>
    )
}

export function Button(props: Omit<MuiButtonProps, 'disabled'> & {
    mode?: 'disabled'|'backward'|'forward',
    inputRef?: React.RefObject<HTMLButtonElement>,
    component?: React.ElementType
}) {
    const context = React.useContext(Context);
    const primary = !context.palette ? undefined : context.palette().primary[
        props.mode === 'disabled' ? 'light'
        : props.mode === 'forward'? 'dark'
        : 'main' 
    ];

    const palette = !context.palette ? undefined 
    : props.variant === 'contained' ? {
        color: context.palette().background,
        backgroundColor: primary
    } : props.variant === 'outlined' ? {
        color: primary,
        backgroundColor: context.palette().background,
        borderColor: primary
    } : {
        color: primary,
        backgroundColor: context.palette().background
    };
    return <ErrorBoundary fallback={<div>Internal error: unable to render Styler.Button</div>}>
        <MuiButton {...props} ref={props.inputRef} style={{ ...palette, ...props.style }}>{props.children}</MuiButton>
    </ErrorBoundary>;
}

export function IconButton(props: Omit<MuiIconButtonProps, 'disabled'> & {
    mode?: 'disabled'|'backward'|'forward',
    inputRef?: React.RefObject<HTMLButtonElement>,
}) {
    const context = React.useContext(Context);
    const palette = !context.palette ? undefined : {
        color: context.palette().primary[
            props.mode === 'disabled' ? 'light'
            : props.mode === 'forward'? 'dark'
            : 'main' 
        ],
        backgroundColor: context.palette().background
    };
    return <ErrorBoundary fallback={<div>Internal error: unable to render Styler.IconButton</div>}>
        <MuiIconButton {...props} ref={props.inputRef} style={{ ...palette, ...props.style }}>{props.children}</MuiIconButton>
    </ErrorBoundary>;
}

export function Input(props: MuiInputProps & {
    inputRef?: React.RefObject<HTMLButtonElement>,
    component?: string
}) {
    const context = React.useContext(Context);
    const palette = !context.palette ? undefined : {
        color: context.palette().primary[props.disabled === true ? 'light' : 'main' ],
        backgroundColor: context.palette().background
    };
    return <ErrorBoundary fallback={<div>Internal error: unable to render Styler.Input</div>}>
        <MuiInput {...props} ref={props.inputRef} style={{ ...palette, ...props.style }}/>
    </ErrorBoundary>;
}

export function Card(props: React.PropsWithChildren<{
    prefix: string,
    style?: React.CSSProperties,
    theme?: Theme,
    viewport?: React.RefObject<HTMLDivElement>,
    onClick?: React.MouseEventHandler<HTMLDivElement>,
}>) {
    const _context = React.useContext(Context);
    const [ inView, setInView ] = React.useState(true);
    const ref = React.useRef<HTMLDivElement>(null);
    const theme = props.theme || useTheme();
    if(theme === undefined) throw new Error('Mising Context');

    React.useEffect(() => {
        if(props.viewport?.current && ref.current) {
            const observer_ = (new IntersectionObserver(entries => {
                let inView_ = false;
                entries.forEach(entry => {
                    if(entry.intersectionRatio > 0.5) {
                        inView_ = true;
                    }
                })
                setInView(inView_);
            }, {
                root: props.viewport.current,
                rootMargin: "50%",
                threshold: [
                    0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1
                ],
            }));
            observer_.observe(ref.current);
            return () => observer_.disconnect();
        }
    }, [ props.viewport?.current, ref.current ]);

    return (  
        <ErrorBoundary fallback={<div>Internal error: unable to render Styler.Card</div>}>      
        <ThemeProvider theme={theme}>
        <Context.Provider value={{
            ..._context,
            palette: () => ({
                background: theme.palette.background.default,
                primary: theme.palette.primary,
                secondary: theme.palette.secondary
            }),
            typography: () => ({
                heading: (props) => <Typography {...props} variant="h2" color={'primary'}>{props.children}</Typography>,
                body: (props) => <Typography {...props} variant="body1" color={'primary'}>{props.children}</Typography>,
                value: (props) => <Typography {...props} variant="body2" color={'primary'}>{props.children}</Typography>,
            })            
        }}>
            <MuiCard id={`${props.prefix}-root`} ref={ref} style={{ backgroundColor: theme.palette.background.default, ...props.style }} onClick={props.onClick}>
                {
                    !inView ? <React.Fragment></React.Fragment> : props.children
                }
            </MuiCard>
        </Context.Provider>
        </ThemeProvider>
        </ErrorBoundary>
    )
}

export function CardMedia(props: React.PropsWithChildren<{
    style?: React.CSSProperties
}>) {
    return (
        <MuiCardMedia style={props.style}>{props.children}</MuiCardMedia>
    )
}

export function CardContent(props: React.PropsWithChildren<{
    style?: React.CSSProperties
}>) {
    return (
        <MuiCardContent style={props.style}>{props.children}</MuiCardContent>
    )
}

export function CardActions(props: React.PropsWithChildren<{
    style?: React.CSSProperties
}>) {
    return (
        <MuiCardActions style={props.style}>{props.children}</MuiCardActions>
    )
}

function _Chip(props: MuiChipProps) {
    const _context = React.useContext(Context);
    return <MuiChip {...props} style={{ 
        margin: '0.5em', 
        backgroundColor: _context.palette && _context.palette().background,
        color: _context.palette && _context.palette().primary.main,
        ...props.style
    }}>{props.children}</MuiChip>
}

export function Chip(props: MuiChipProps & {
    theme?: Theme
}) {
    const _context = React.useContext(Context);
    const theme_ = props.theme || useTheme();
    if(theme_ === undefined) throw new Error('Mising Context');
    return <ErrorBoundary fallback={<div>Internal error: unable to render Styler.Chip</div>}>
        <ThemeProvider theme={theme_}>
        <Context.Provider value={{
            ..._context,
            palette: () => ({
                background: theme_.palette.background.default,
                primary: theme_.palette.primary,
                secondary: theme_.palette.secondary
            })            
        }}>
            <_Chip {...props}/>
        </Context.Provider>
    </ThemeProvider></ErrorBoundary>;
}

export function Chips(props: Omit<MuiChipProps, 'children'> & React.PropsWithChildren<{
    limit?: number,
    theme?: Theme
}>) {
    const _context = React.useContext(Context);
    const theme_ = props.theme || useTheme();
    if(theme_ === undefined) throw new Error('Mising Context');
    const children = React.Children.toArray(props.children) || [];
    const extras = props.limit === undefined || props.limit >= children.length ? undefined : `+ ${children.length - props.limit}`;
    return <ThemeProvider theme={theme_}>
        <Context.Provider value={{
            ..._context,
            palette: () => ({
                background: theme_.palette.background.default,
                primary: theme_.palette.primary,
                secondary: theme_.palette.secondary
            }),
            typography: () => ({
                heading: (props) => <Typography {...props} variant="h2" color={'primary'}>{props.children}</Typography>,
                body: (props) => <Typography {...props} variant="body1" color={'primary'}>{props.children}</Typography>,
                value: (props) => <Typography {...props} variant="body2" color={'primary'}>{props.children}</Typography>,
            })            
        }}>
        { children.filter((child, index) => props.limit === undefined || index < props.limit).map(child => <Chip {...{...props, children: null }} label={child}/>) }
        {  extras === undefined ? <React.Fragment></React.Fragment> : <Chip {...{...props, children: null}} label={extras}/>}
        </Context.Provider>
    </ThemeProvider>;
}

export function Section(props: React.PropsWithChildren<{
    prefix: string,
    style?: React.CSSProperties,
    theme?: Theme
}>) {
    const _context = React.useContext(Context);
    const theme_ = props.theme || useTheme();
    if(theme_ === undefined) throw new Error('Mising Context');
      
    return (
        <ErrorBoundary fallback={<div>Internal error: unable to render Styler.Section</div>}>
        <ThemeProvider theme={theme_}>
        <Context.Provider value={{
            ..._context,
            palette: () => ({
                background: theme_.palette.background.default,
                primary: theme_.palette.primary,
                secondary: theme_.palette.secondary
            }),
            typography: () => ({
                heading: (props) => <Typography {...props} variant="h3" color={'primary'}>{props.children}</Typography>,
                body: (props) => <Typography {...props} variant="body1" color={'primary'}>{props.children}</Typography>,
                value: (props) => <Typography {...props} variant="body2" color={'primary'}>{props.children}</Typography>,
            })
        
        }}><div id={`${props.prefix}-section`} style={props.style}>{props.children}</div></Context.Provider>
        </ThemeProvider>
        </ErrorBoundary>
    )
}

export function Control(props: React.PropsWithChildren<{
    prefix: string,
    style?: React.CSSProperties,
    theme?: Theme
}>) {
    const _context = React.useContext(Context);
    const theme_ = props.theme || useTheme();
    if(theme_ === undefined) throw new Error('Mising Context');
      
    return (
        <ErrorBoundary fallback={<div>Internal error: unable to render Styler.Control</div>}>
        <ThemeProvider theme={theme_}>
        <Context.Provider value={{
            ..._context,
            palette: () => ({
                background: theme_.palette.background.default,
                primary: theme_.palette.primary,
                secondary: theme_.palette.secondary
            }),
            typography: () => ({
                heading: (props) => <Typography {...props} variant="h4" color={'primary'}>{props.children}</Typography>,
                body: (props) => <Typography {...props} variant="body1" color={'primary'}>{props.children}</Typography>,
                value: (props) => <Typography {...props} variant="body2" color={'primary'}>{props.children}</Typography>,
            })
        
        }}><div id={`${props.prefix}-root`} style={props.style}>{props.children}</div></Context.Provider>
        </ThemeProvider>
        </ErrorBoundary>
    )
}

export function Heading(props: React.PropsWithChildren<ElementProps>): JSX.Element {
    const context = React.useContext(Context);
    if(!context.typography) {
        console.error('Styler.Heading must appear inside of Styler.{Screen|Section|Control}')
        return <React.Fragment>{props.children}</React.Fragment>
    }
    return (context.typography().heading(props));
}

export function Body(props: React.PropsWithChildren<ElementProps>): JSX.Element {
    const context = React.useContext(Context);
    if(!context.typography) {
        console.error('Styler.Body must appear inside of Styler.{Screen|Section|Control}')
        return <React.Fragment>{props.children}</React.Fragment>
    }
    return (context.typography().body(props));
}

export function Value(props: React.PropsWithChildren<ElementProps>): JSX.Element {
    const context = React.useContext(Context);
    if(!context.typography) {
        console.error('Styler.Value must appear inside of Styler.{Screen|Section|Control}')
        return <React.Fragment>{props.children}</React.Fragment>
    }
    return (context.typography().value(props));
}

export function Bullet(props: ElementProps): JSX.Element {
    const context = React.useContext(Context);
    return (<span style={{ margin: '0.5em' }}>{context.chars().bullet}</span>);
}

export function Skeleton(props: MuiSkeletonProps): JSX.Element {
    const _context = React.useContext(Context);
    return <MuiSkeleton animation="wave" variant="rect" style={{
        backgroundColor: _context.palette && _context.palette().primary.main
    }} {...props}/>
}

